import React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { motion } from "framer-motion";
import { transition } from "../../../utils/transition";
import { fadeIn } from "../../../utils/variants";

const BRAND = { navy: "#062b6c", green: "#00ad0f" };

const SERVICES = [
  { id: 1, name: "Environmental And Waste Management Services" },
  { id: 2, name: "Wellhead Installation and Maintenance Service" },
  { id: 3, name: "Equipment Leasing And Procurement" },
  { id: 4, name: "Oil And Gas Drilling" },
  { id: 5, name: "Well Overhauling / Stimulation Services" },
  {
    id: 6,
    name: "Hydraulic, Stimulation Coiled Tubing / Nitrogen Services",
  },
  { id: 7, name: "Pumping Services" },
  { id: 8, name: "Electrical Logging / Calibration Services" },
  { id: 9, name: "Open Hole, Cased Hole, Slim Hole" },
  { id: 10, name: "Bolt Tensioning Services" },
  { id: 11, name: "Hydraulic Torqueing" },
  { id: 12, name: "Casing Cutting Services" },
  {
    id: 13,
    name: "Chemical Cold, Explosive, Field Maintenance Services",
  },
  { id: 14, name: "Rental Of Drill Cutting Skips" },
  { id: 15, name: "Pipping Line Welding And Fabrications Services" },
  { id: 16, name: "Logistics Services" },
  { id: 17, name: "Environmental Consultancy" },
  { id: 18, name: "Civil And Mechanical Engineering" },
  { id: 19, name: "Constructions" },
  { id: 20, name: "Location Preparations" },
];

const App = () => {
  return (
    <div className="servicePageDiv bg-[#020617] text-slate-50">
      {/* HERO – totally different layout from your original */}
      <section className="relative overflow-hidden pb-10 pt-24 md:pt-28 lg:pt-32">
        {/* background image pane */}
        <div className="absolute inset-0">
          <div className="absolute right-0 top-0 h-full w-full max-w-xl opacity-50 lg:opacity-80">
            <img
              src="/new_img/backdrop.jpg"
              alt="Phronesis operations"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-[#020617] via-[#020617]/80 to-transparent" />
          </div>
          {/* soft grid + glows */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,#0b1120_1px,transparent_1px),linear-gradient(to_bottom,#0b1120_1px,transparent_1px)] [background-size:42px_42px]" />
          <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-[#00ad0f]/20 blur-3xl" />
          <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-[#1d4ed8]/25 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-center">
            {/* Left: text block */}
            <motion.div
              variants={fadeIn("Up")}
              transition={transition()}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-5"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-[#00ad0f]/50 bg-[#020617]/80 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#bbffcc]">
                <span
                  className="h-2 w-2 rounded-full shadow-[0_0_10px_rgba(0,173,15,0.9)]"
                  style={{ background: BRAND.green }}
                />
                Phronesis Oil &amp; Gas Tek • Scope of Work
              </div>

              <div className="flex items-center gap-3">
                <span
                  className="h-12 w-[3px] rounded-full"
                  style={{ background: BRAND.navy }}
                />
                <div>
                  <h1 className="text-3xl font-semibold uppercase md:text-4xl lg:text-[2.6rem] lg:leading-tight">
                    Integrated Field{" "}
                    <span className="bg-gradient-to-r from-[#00ad0f] to-[#7fffb0] bg-clip-text text-transparent">
                      Service Scope
                    </span>
                  </h1>
                  <p className="mt-3 max-w-xl text-sm text-slate-200 md:text-[0.96rem]">
                    A consolidated overview of Phronesis’ operational
                    capabilities across drilling, well services, environmental
                    management, logistics, and integrated engineering support.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 text-[11px] md:grid-cols-3">
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-3">
                  <p className="uppercase tracking-[0.22em] text-emerald-200">
                    20+ Scope Lines
                  </p>
                  <p className="mt-1 text-[11px] text-emerald-50">
                    Covering drilling, stimulation, maintenance &amp; logistics.
                  </p>
                </div>
                <div className="rounded-2xl border border-sky-400/30 bg-sky-400/10 px-3 py-3">
                  <p className="uppercase tracking-[0.22em] text-sky-200">
                    Integrated Delivery
                  </p>
                  <p className="mt-1 text-[11px] text-sky-50">
                    Packaged services engineered for efficiency and safety.
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-500/40 bg-slate-900/70 px-3 py-3">
                  <p className="uppercase tracking-[0.22em] text-slate-200">
                    Field Proven
                  </p>
                  <p className="mt-1 text-[11px] text-slate-100">
                    Built from real-world project experience and operations.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-1">
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-600/70 bg-slate-900/60 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-slate-200">
                  <ArrowForwardIcon className="h-4 w-4" />
                  Scroll to view full scope listing
                </span>
              </div>
            </motion.div>

            {/* Right: compact image panel */}
            <motion.div
              variants={fadeIn("Up")}
              transition={transition()}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="hidden h-64 overflow-hidden rounded-3xl border border-white/10 bg-slate-900/60 shadow-[0_24px_80px_rgba(0,0,0,0.8)] lg:block"
            >
              <div className="relative h-full w-full">
                <img
                  src="/new_img/backdrop.jpg"
                  alt="Phronesis operations"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[11px] text-slate-100">
                  <div className="max-w-xs">
                    <span className="block text-[10px] uppercase tracking-[0.26em] text-slate-300">
                      Field Operations Snapshot
                    </span>
                    <span className="text-sm">Drilling &amp; Well Services</span>
                  </div>
                  <span className="rounded-full bg-black/50 px-3 py-1 text-[10px] uppercase tracking-[0.22em]">
                    Phronesis Scope
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SCOPE GRID – new “mission cards” layout */}
      <section className="relative pb-16 pt-4">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.26em] text-slate-400">
                Scope Catalogue
              </p>
              <h2 className="text-lg font-semibold text-slate-50 md:text-xl">
                Structured view of Phronesis’ operational scope.
              </h2>
            </div>
            <span className="text-[11px] text-slate-400">
              Total scope items: {SERVICES.length.toString().padStart(2, "0")}
            </span>
          </div>

          <motion.ul
            variants={fadeIn("Up")}
            transition={transition()}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
          >
            {SERVICES.map(({ id, name }) => (
              <li key={id} className="h-full">
                <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-700/70 bg-[#020819]/80 shadow-[0_18px_45px_rgba(0,0,0,0.75)] transition-transform hover:-translate-y-1 hover:border-[#00ad0f]/70">
                  {/* top strip */}
                  <div className="flex items-center justify-between border-b border-slate-700/70 px-4 py-3 text-[11px]">
                    <span className="inline-flex items-center gap-2 text-slate-300">
                      <span className="font-mono text-[10px] text-[#7fffb0]">
                        {id.toString().padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.22em] text-slate-400">
                        Scope Line
                      </span>
                    </span>
                    <span
                      className="h-2 w-2 rounded-full shadow-[0_0_10px_rgba(0,173,15,0.9)]"
                      style={{ background: BRAND.green }}
                    />
                  </div>

                  {/* body */}
                  <div className="flex flex-1 flex-col justify-between px-4 py-4">
                    <p className="text-[13px] font-semibold text-slate-50">
                      {name}
                    </p>

                    <div className="mt-4 flex items-center justify-between text-[11px] text-slate-400">
                      <span className="uppercase tracking-[0.22em]">
                        Phronesis • Oil &amp; Gas
                      </span>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-600 bg-slate-900/70 text-xs text-slate-200 transition group-hover:border-[#00ad0f] group-hover:text-[#bbffcc]"
                      >
                        <span className="sr-only">View scope detail</span>
                        <span>&rarr;</span>
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>
    </div>
  );
};

export default App;
